import { useState } from 'react';
import TitlePage from '../../../components/TitlePage';
import { Column, TableComponent } from '../../../components/TableComponent';
import LoadingCircle from '@/components/LoadingCircle';
import { formatDate } from '@/lib/utils';
import { useCrawlFbList } from '../../../hooks/useCrawlFb';
import { CrawlFbResponse, CrawlFbStatus } from '../../../api/crawlFb';
import CrawlDetailModal from './components/CrawlDetailModal';
import ImagePreviewModal from '../CrawlFb/components/ImagePreviewModal';
import { useQueryClient } from '@tanstack/react-query';
import Delete from './components/Delete';

const limit = 10;

const ListCrawFb = () => {
  const [page, setPage] = useState(1);
  const [selectedCrawl, setSelectedCrawl] = useState<CrawlFbResponse | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    imageUrl: string;
    title: string;
    images?: { url: string; title?: string }[];
    currentIndex?: number;
  } | null>(null);

  const queryClient = useQueryClient();
  const { data, isLoading, isFetching } = useCrawlFbList({
    page,
    limit,
  });

  const handleIdClick = (crawl: CrawlFbResponse) => {
    setSelectedCrawl(crawl);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCrawl(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingCircle />
      </div>
    );
  }

  const getStatusColor = (status: CrawlFbStatus) => {
    switch (status) {
      case CrawlFbStatus.COMPLETED:
        return 'text-green-600 bg-green-100';
      case CrawlFbStatus.FAILED:
        return 'text-red-600 bg-red-100';
      case CrawlFbStatus.PROCESSING:
        return 'text-blue-600 bg-blue-100';
      case CrawlFbStatus.PENDING:
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status: CrawlFbStatus) => {
    switch (status) {
      case CrawlFbStatus.COMPLETED:
        return 'Hoàn thành';
      case CrawlFbStatus.FAILED:
        return 'Thất bại';
      case CrawlFbStatus.PROCESSING:
        return 'Đang xử lý';
      case CrawlFbStatus.PENDING:
        return 'Chờ xử lý';
      default:
        return status;
    }
  };

  const columns: Column<CrawlFbResponse>[] = [
    {
      header: 'ID',
      accessorKey: 'id',
      cell: (item) => {
        return (
          <div
            className="font-mono text-sm text-blue-600 hover:text-blue-800 cursor-pointer hover:underline"
            onClick={() => handleIdClick(item)}
            title="Click để xem chi tiết"
          >
            {item.id.slice(0, 8)}...
          </div>
        );
      },
    },
    {
      header: 'URL Crawl',
      accessorKey: 'urlCrawl',
      cell: (item) => {
        return (
          <div className="max-w-[300px] truncate">
            <a
              href={item.urlCrawl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {item.urlCrawl}
            </a>
          </div>
        );
      },
    },
    {
      header: 'Trạng thái',
      accessorKey: 'status',
      cell: (item) => {
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
              item.status
            )}`}
          >
            {getStatusLabel(item.status)}
          </span>
        );
      },
    },
    {
      header: 'Ảnh Posts',
      cell: (item) => {
        return (
          <div className="text-center">{item.postImages?.length || 0}</div>
        );
      },
    },
    {
      header: 'Ảnh About',
      cell: (item) => {
        return (
          <div className="text-center">{item.aboutImages?.length || 0}</div>
        );
      },
    },
    {
      header: 'Friends URLs',
      cell: (item) => {
        return (
          <div className="text-center">{item.friendsUrls?.length || 0}</div>
        );
      },
    },
    {
      header: 'Ngày tạo',
      accessorKey: 'createdAt',
      cell: (item) => {
        return <div>{formatDate(item.createdAt)}</div>;
      },
    },
    {
      header: 'Cập nhật',
      accessorKey: 'updatedAt',
      cell: (item) => {
        return <div>{formatDate(item.updatedAt)}</div>;
      },
    },
    {
      header: 'Thao tác',
      cell: (item) => {
        return (
          <div className="flex items-center justify-center gap-2">
            <Delete
              crawl={item}
              onSuccess={() => {
                if (data?.crawls?.length === 1 && page === data?.pages) {
                  setPage(page - 1);
                } else {
                  queryClient.invalidateQueries({
                    queryKey: ['crawl-fb-list'],
                  });
                }
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="luxury-theme min-h-screen">
      <div className="flex items-center justify-between bg-white p-[18px] border-b border-luxury-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="text-2xl text-luxury-900">
            <TitlePage />
          </div>
        </div>
      </div>

      <div className="px-6 py-6 relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
            <LoadingCircle />
          </div>
        )}
        <TableComponent
          data={data?.crawls || []}
          columns={columns}
          pageSize={limit}
          totalPages={data?.pages || 0}
          onPageChange={setPage}
          currentPage={page}
          className="luxury-table"
        />
      </div>

      <CrawlDetailModal
        isOpen={isModalOpen}
        onClose={selectedImage ? () => {} : handleCloseModal}
        crawlData={selectedCrawl}
        onImageSelect={setSelectedImage}
      />

      {/* Image Preview Modal - Top level, outside all other modals */}
      <ImagePreviewModal
        isOpen={!!selectedImage}
        imageUrl={selectedImage?.imageUrl || null}
        onClose={() => setSelectedImage(null)}
        title={selectedImage?.title || 'Preview'}
        images={selectedImage?.images}
        currentIndex={selectedImage?.currentIndex}
        onImageChange={(index) => {
          if (selectedImage?.images) {
            setSelectedImage({
              ...selectedImage,
              imageUrl: selectedImage.images[index].url,
              currentIndex: index,
              title: selectedImage.images[index].title || selectedImage.title,
            });
          }
        }}
      />
    </div>
  );
};

export default ListCrawFb;
